import { useEffect, useState } from 'react';
import { send } from '@/shared/messages';
import type { Chapter } from '@/shared/types';
import { PermissionsPanel } from '../components/PermissionsPanel';
import { VttCaptureRow, type VttCapture, type IngestArgs } from '../components/VttCaptureRow';
import { Alert } from '@/components/Alert';

export function VideosRoute({
  onCaptured,
  activeTestId,
}: {
  onCaptured: (chapterId: string) => void;
  activeTestId: string | null;
}) {
  const [captures, setCaptures] = useState<VttCapture[]>([]);
  const [chapters, setChapters] = useState<Chapter[]>([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function refresh() {
    const [vttRes, chapRes] = await Promise.all([
      send({ type: 'LIST_RECENT_VTTS' }),
      // Scope the attach-target list to the active test, matching where a new
      // chapter created from a VTT would land.
      send({ type: 'LIST_CHAPTERS', testId: activeTestId ?? undefined }),
    ]);
    setCaptures(vttRes.captures);
    setChapters(chapRes.chapters);
    setLoading(false);
  }

  useEffect(() => {
    void refresh();
    const listener = (msg: { type?: string }) => {
      if (msg?.type === 'VTT_BUFFER_UPDATED' || msg?.type === 'INGEST_DONE') {
        void refresh();
      }
    };
    chrome.runtime.onMessage.addListener(listener);
    return () => chrome.runtime.onMessage.removeListener(listener);
  }, [activeTestId]);

  async function clearAll() {
    if (!confirm('Clear the recent VTT buffer?')) return;
    await send({ type: 'CLEAR_RECENT_VTTS' });
  }

  async function ingest(args: IngestArgs) {
    setBusyId(args.capture.id);
    setError(null);
    try {
      const r = await send({
        type: 'ATTACH_VTT_CAPTURE',
        vttId: args.capture.id,
        chapterTitle: args.chapterTitle,
        targetChapterId: args.targetChapterId,
        targetTestId: activeTestId ?? undefined,
      });
      onCaptured(r.chapterId);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusyId(null);
    }
  }

  if (loading) {
    return <div className="p-4 text-sm text-slate-500">Loading…</div>;
  }

  return (
    <div className="space-y-3 p-4">
      <div className="flex items-start justify-between gap-2">
        <div className="space-y-1">
          <h2 className="text-sm font-semibold text-slate-800">Recent video subtitles</h2>
          <p className="text-xs text-slate-600">
            The extension watches network traffic on LMS hosts you've granted access to and
            remembers VTT subtitle URLs. Open a video on the LMS, then come back here to
            capture its transcript.
          </p>
        </div>
        <button
          onClick={clearAll}
          disabled={captures.length === 0}
          className="shrink-0 rounded border border-slate-300 px-2 py-1 text-xs text-slate-700 hover:border-red-300 hover:bg-red-50 hover:text-red-700 disabled:opacity-40"
          title="Clear the recent VTT buffer"
        >
          Clear buffer ({captures.length})
        </button>
      </div>

      <PermissionsPanel />

      {captures.length === 0 ? (
        <div className="rounded border border-dashed border-slate-300 p-4 text-center text-xs text-slate-500">
          No VTTs captured yet. Play a video on your LMS, then return here.
          <br />
          (If you just installed the extension, you may need to grant host access above.)
        </div>
      ) : (
        <div className="space-y-2">
          {captures.map((c) => (
            <VttCaptureRow
              key={c.id}
              capture={c}
              chapters={chapters}
              busy={busyId === c.id}
              disabled={busyId !== null && busyId !== c.id}
              onIngest={ingest}
            />
          ))}
        </div>
      )}

      {error && (
        <Alert>
          <div className="font-medium">Capture failed</div>
          <div>{error}</div>
        </Alert>
      )}

    </div>
  );
}
